/**
 * @fileoverview Dropdown component for the Storyteller web application.
 * Displays a dropdown menu with options to select from.
 *
 * @example
 * // Basic usage
 * <Dropdown options={[{ const: 'option1', title: 'Option 1' }]} onChange={() => {}} />
 */

import { Listbox } from '@headlessui/react'
import config from '../../config'
import css from './Dropdown.module.css'

/**
 * @interface DropdownProps
 * @description Props for the Dropdown component.
 * @property {Function} onChange - A function to handle the change action.
 * @property {Object[]} options - The options to display in the dropdown.
 * @property {string} [placeholder] - The placeholder text for the dropdown.
 * @property {boolean} [disabled] - Whether the dropdown is disabled.
 * @property {string} [value] - The value of the selected option.
 * @property {string} path - The path of the dropdown.
 * @property {boolean} [required] - Whether the dropdown is required.
 */
interface DropdownProps extends React.HTMLAttributes<HTMLDivElement> {
    onChange: (value: unknown) => void
    options: { const?: string; title?: string; icon?: string }[]
    placeholder?: string
    disabled?: boolean
    value?: string
    path: string
    required?: boolean
    errors?: string
    hideErrorMessage?: boolean
}

/**
 * @function classNames
 * @description Helper function to concatenate class names with spaces.
 * @param {...string[]} classes - The class names to concatenate.
 * @returns {string} The concatenated class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function Dropdown
 * @description Component for displaying a dropdown menu with options to select from.
 * @param {DropdownProps} props - The props for the Dropdown component.
 * @returns {JSX.Element} The Dropdown component.
 */
export function Dropdown(props: DropdownProps) {
    const { options, hideErrorMessage, placeholder, value, errors = '', onChange, path } = props
    const hasError = errors.length > 0
    const current = options.find((option) => option.const === value)

    return (
        <div className={classNames(css.container, props.className)}>
            <Listbox
                disabled={options.length < 2}
                as="div"
                value={value}
                id={path}
                name={path}
                className={css.listbox}
                onChange={onChange}
            >
                <Listbox.Button
                    className={classNames(
                        css.trigger,
                        hasError && css.error,
                        !current && css.placeholder
                    )}
                >
                    {current?.icon && (
                        <img
                            src={config.s3.getStaticURL(current?.icon)}
                            alt={current?.title}
                            className={css.icon}
                        />
                    )}
                    <span>{current?.title || placeholder}</span>
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={css.chevron}
                    >
                        <path
                            d="M19 9L12 16L5 9"
                            stroke="#737373"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </Listbox.Button>
                <Listbox.Options className={css.options}>
                    {options.map((option: { const: string; title: string; icon: string }) => (
                        <Listbox.Option
                            disabled={current?.const === option.const}
                            key={option.const}
                            value={option.const}
                        >
                            {({ active, disabled }) => (
                                <div
                                    className={classNames(
                                        active && css.active,
                                        css.option,
                                        current?.const === option.const && css.selected
                                    )}
                                >
                                    {option.icon && (
                                        <img
                                            src={config.s3.getStaticURL(option.icon)}
                                            alt={option.title}
                                            className={css.icon}
                                        />
                                    )}
                                    <span className={classNames(disabled && css.disabled)}>
                                        {option.title}
                                    </span>
                                </div>
                            )}
                        </Listbox.Option>
                    ))}
                </Listbox.Options>
            </Listbox>
            {hasError && !hideErrorMessage && <div className={css.errorMessage}>{errors}</div>}
        </div>
    )
}
